/**
 * StepFieldConfig 核心 hook：列合并、维度/指标切换、行过滤、下拉选项、内联新增确认。
 * 表格列 JSX 见 `useFieldConfigTableColumns.tsx`。
 */
import { useCallback, useMemo } from 'react';

import type {
  IYamlFieldColumn,
} from '../../../types';
import type {
  IUseStepFieldConfigCoreParams,
  IUseStepFieldConfigCoreResult,
} from '../types';
import {
  isDimInlineAddDraftValid,
  isMeasInlineAddDraftValid,
  makeCustomCubeFieldName,
} from '../../../utils/fieldConfigRowUtils';
import {
  coerceDimTypeSelectValue,
  DIM_DATA_TYPES,
  DIM_TYPE_ID,
  MEAS_DATA_TYPES,
  MEAS_TYPE_ID,
} from '../constants';

/** 聚合 `patchColumn`、行过滤、options 与内联确认，供页面与列 hook 使用 */
export const useStepFieldConfigCore = (
  params: IUseStepFieldConfigCoreParams,
): IUseStepFieldConfigCoreResult => {
  const {
    fieldColumns,
    fieldsValue,
    onChange,
    formatMessage,
    dimAddDraft,
    measAddDraft,
    changeDimInlineOpen,
    changeMeasInlineOpen,
  } = params;

  /** 单列合并写回：在 `fieldColumns` 中找到 `source_column` 后浅合并 `patch` */
  const patchColumn = useCallback(
    (sourceColumn: string, patch: Partial<IYamlFieldColumn>): void => {
      onChange({
        fieldColumns: fieldColumns.map((c) =>
          c.source_column === sourceColumn ? { ...c, ...patch } : c,
        ),
      });
    },
    [fieldColumns, onChange],
  );

  /**
   * 表格「字段类型」列：在维度 / 指标间切换。
   * 切到指标时补齐 `filters_text`、默认聚合等；切到维度时用 `coerceDimTypeSelectValue` 规范 type。
   */
  const handleFieldKindChange = useCallback(
    (sourceColumn: string, nextKindRaw: unknown): void => {
      const col = fieldColumns.find((c) => c.source_column === sourceColumn);
      if (!col) {
        return;
      }
      const nextKind = nextKindRaw === 'measure' ? 'measure' : 'dimension';
      if (nextKind === col.default_kind) {
        return;
      }
      if (nextKind === 'measure') {
        const t = col.default_type || '';
        patchColumn(sourceColumn, {
          default_kind: 'measure',
          default_type: MEAS_DATA_TYPES.includes(t) ? t : 'sum',
          filters_text: col.filters_text ?? '',
          default_granularity: col.default_granularity ?? '',
        });
      } else {
        const t = col.default_type || '';
        patchColumn(sourceColumn, {
          default_kind: 'dimension',
          default_type: coerceDimTypeSelectValue(t),
        });
      }
    },
    [fieldColumns, patchColumn],
  );

  /** 勾选且 `default_kind === 'dimension'` 的行，供维度表展示 */
  const dimRows = useMemo(
    () =>
      fieldColumns.filter(
        (c) =>
          c.default_kind === 'dimension' &&
          fieldsValue.includes(c.source_column),
      ),
    [fieldColumns, fieldsValue],
  );

  /** 勾选且 `default_kind === 'measure'` 的行，供指标表展示 */
  const measRows = useMemo(
    () =>
      fieldColumns.filter(
        (c) =>
          c.default_kind === 'measure' &&
          fieldsValue.includes(c.source_column),
      ),
    [fieldColumns, fieldsValue],
  );

  /** Tea Select：`dimension` / `measure` 文案 */
  const kindFieldOptions = useMemo(
    () => [
      {
        value: 'dimension',
        text: formatMessage({ id: 'dbl_model_agent_step2_kind_dimension' }),
      },
      {
        value: 'measure',
        text: formatMessage({ id: 'dbl_model_agent_step2_kind_metric' }),
      },
    ],
    [formatMessage],
  );

  /** 指标格式列：空为不设置、可选百分比 */
  const measFormatOptions = useMemo(
    () => [
      {
        value: '',
        text: formatMessage({ id: 'dbl_model_agent_step2_option_unset' }),
      },
      {
        value: 'percent',
        text: formatMessage({ id: 'dbl_model_agent_step2_option_format_percent' }),
      },
    ],
    [formatMessage],
  );

  /** 指标粒度列：与 Cube 文档常见粒度对齐的可选项 */
  const measGranularityOptions = useMemo(
    () => [
      {
        value: '',
        text: formatMessage({ id: 'dbl_model_agent_step2_option_unset' }),
      },
      {
        value: 'hour',
        text: formatMessage({ id: 'dbl_model_agent_step2_option_granularity_hour' }),
      },
      {
        value: 'day',
        text: formatMessage({ id: 'dbl_model_agent_step2_option_granularity_day' }),
      },
      {
        value: 'month',
        text: formatMessage({ id: 'dbl_model_agent_step2_option_granularity_month' }),
      },
    ],
    [formatMessage],
  );

  /** 维度 `default_type` 下拉：顺序与 Cube 文档一致 */
  const dimTypeOptions = useMemo(
    () =>
      DIM_DATA_TYPES.map((v) => ({
        value: v,
        text: formatMessage({
          id:
            DIM_TYPE_ID[v] ??
            'dbl_model_agent_field_config_dim_data_type_string',
        }),
      })),
    [formatMessage],
  );

  /** 指标聚合类型下拉：`sum` / `count` / … */
  const measTypeOptions = useMemo(
    () =>
      MEAS_DATA_TYPES.map((v) => ({
        value: v,
        text: formatMessage({
          id:
            MEAS_TYPE_ID[v] ??
            'dbl_model_agent_field_config_measure_agg_sum',
        }),
      })),
    [formatMessage],
  );

  /**
   * 内联新增维度确认：要求 SQL 非空；追加 `is_custom` 行并把新 `source_column` 加入勾选。
   * 表上下文（`table_id` / `table_name`）继承首列，便于与主表 YAML 块对齐。
   */
  const handleConfirmDimInline = useCallback((): void => {
    if (!isDimInlineAddDraftValid(dimAddDraft)) {
      return;
    }
    const sql = dimAddDraft.sql.trim();
    const base = fieldColumns[0];
    const sc = makeCustomCubeFieldName('dimension', fieldColumns);
    const row: IYamlFieldColumn = {
      source_column: sc,
      origin_type: 'unknown',
      default_kind: 'dimension',
      default_name: sc,
      default_display_name: dimAddDraft.displayName.trim() || sc,
      default_sql: sql,
      default_description: dimAddDraft.description,
      default_name_zh: dimAddDraft.nameZh,
      default_name_en: dimAddDraft.nameEn,
      default_type: dimAddDraft.dataType,
      default_common_query: '',
      default_format: '',
      default_granularity: 'day',
      default_sample_values: dimAddDraft.sampleValues,
      default_dim_summary_value: dimAddDraft.dimSummary.trim()
        ? dimAddDraft.dimSummary
        : undefined,
      default_dim_table: dimAddDraft.dimTable.trim()
        ? dimAddDraft.dimTable
        : undefined,
      default_dim_table_column: dimAddDraft.dimJoinColumn.trim()
        ? dimAddDraft.dimJoinColumn
        : undefined,
      is_custom: true,
      table_id: base?.table_id || '',
      table_name: base?.table_name || '',
      is_primary_table: base?.is_primary_table || true,
    };
    onChange({
      fieldColumns: [...fieldColumns, row],
      fieldsValue: [...fieldsValue, sc],
    });
    changeDimInlineOpen(false);
  }, [
    dimAddDraft,
    fieldColumns,
    fieldsValue,
    onChange,
    changeDimInlineOpen,
  ]);

  /**
   * 内联新增指标确认：要求 SQL 与展示名非空；写入 `filters_text` 等默认字段。
   */
  const handleConfirmMeasInline = useCallback((): void => {
    if (!isMeasInlineAddDraftValid(measAddDraft)) {
      return;
    }
    const sql = measAddDraft.sql.trim();
    const disp = measAddDraft.displayName.trim();
    const base = fieldColumns[0];
    const sc = makeCustomCubeFieldName('metric', fieldColumns);
    const row: IYamlFieldColumn = {
      source_column: sc,
      origin_type: 'unknown',
      default_kind: 'measure',
      default_name: sc,
      default_display_name: disp,
      default_sql: sql,
      default_description: measAddDraft.description,
      default_name_zh: measAddDraft.nameZh,
      default_name_en: measAddDraft.nameEn,
      default_type: measAddDraft.dataType,
      default_common_query: measAddDraft.commonQuery,
      default_format: measAddDraft.format || '',
      default_granularity: measAddDraft.granularity,
      is_custom: true,
      table_id: base?.table_id || '',
      table_name: base?.table_name || '',
      is_primary_table: base?.is_primary_table || true,
      filters_text: measAddDraft.filters.trim() || '',
    };
    onChange({
      fieldColumns: [...fieldColumns, row],
      fieldsValue: [...fieldsValue, sc],
    });
    changeMeasInlineOpen(false);
  }, [
    measAddDraft,
    fieldColumns,
    fieldsValue,
    onChange,
    changeMeasInlineOpen,
  ]);

  /** 汇总返回，供 `index` 与 `useFieldConfigTableColumns` 使用 */
  return {
    patchColumn,
    handleFieldKindChange,
    dimRows,
    measRows,
    kindFieldOptions,
    dimTypeOptions,
    measTypeOptions,
    measFormatOptions,
    measGranularityOptions,
    handleConfirmDimInline,
    handleConfirmMeasInline,
  };
};
